import type { CSSProperties, ElementType, ReactElement, ReactNode } from 'react';

/**
 * Bedrock `<Band>` — the marketing site's one section wrapper.
 *
 * Every full-width stripe on every marketing route is a Band. It owns three
 * things a section should never re-decide: the tone (which of the three
 * grounds it paints), the gutter + max-width, and the vertical rhythm. A page
 * is then a stack of Bands, which is what makes ~30 routes read as one site.
 *
 * ## Tones
 *
 * | tone    | ground    | used for                                        |
 * | ------- | --------- | ----------------------------------------------- |
 * | `page`  | `#ffffff` | the default — prose, cards, grids               |
 * | `sunk`  | `#fafafa` | a quiet alternating stripe                      |
 * | `band`  | `#08090c` | a dark inset panel (rounded, inside the gutter) |
 * | `black` | `#000000` | full-bleed dark — the story demo only           |
 *
 * `band` and `black` differ by more than colour: `band` is a ROUNDED CARD that
 * sits inside the page gutter (the design's "how it works" and "control"
 * blocks), while `black` bleeds edge-to-edge (the story). Passing a rounded
 * tone therefore also switches the padding model, so callers never hand-roll
 * `rounded-[26px] bg-[#08090c] p-12` and drift.
 *
 * ## Ambient
 *
 * Dark tones may carry an ambient wash (a soft brand radial) and a masked dot
 * field. Both are `pointer-events:none` decoration behind the content, and
 * both are suppressed under `prefers-reduced-motion` by the global rule in
 * `bedrock.css` — the wash animates.
 */

export type BandTone = 'page' | 'sunk' | 'band' | 'black';

/** Vertical rhythm. `none` lets a caller own its own padding entirely. */
export type BandSpace = 'none' | 'sm' | 'md' | 'lg';

/**
 * Which left rule a FLAT band's content column starts on.
 *
 * The two padding models put content in different places by construction, and
 * both are right on their own terms: a stripe pads its column by the page
 * gutter, while a card is ITSELF inset by that gutter and then pads its content
 * again from its own edge. Inside a card that extra step is correct — there is a
 * visible edge to be padded from. Between a card and a flat stripe there is no
 * edge, so the same difference reads as a misalignment.
 *
 * - `stripe` (default) — the gutter. What every flat band has always used, and
 *   what a page made only of stripes should keep.
 * - `card` — the rule an inset card's CONTENT sits on. For a page that stacks
 *   both and needs one left rule down the whole thing. Measured on the landing
 *   page at 1440px: stripe titles at 127px against card titles at 151px.
 *
 * Ignored on an inset tone, which is already on the card rule.
 */
export type BandAlign = 'stripe' | 'card';

export interface BandProps {
  readonly tone?: BandTone;
  readonly space?: BandSpace;
  /** Which left rule the content column starts on. See {@link BandAlign}. */
  readonly align?: BandAlign;
  /** Semantic element — pass `header`/`footer` to keep the landmark. */
  readonly as?: ElementType;
  readonly id?: string;
  /** Ambient brand wash + dot field. Dark tones only; ignored on light ones. */
  readonly ambient?: boolean;
  /** Extra classes on the INNER content wrapper (the max-width column). */
  readonly className?: string;
  /** Extra classes on the OUTER stripe (rarely needed). */
  readonly outerClassName?: string;
  readonly style?: CSSProperties;
  readonly children: ReactNode;
  readonly 'aria-labelledby'?: string;
  readonly 'aria-label'?: string;
}

const GROUND: Record<BandTone, string> = {
  page: 'bg-[color:var(--bd-page)]',
  sunk: 'bg-[color:var(--bd-sunk)]',
  band: 'bg-[color:var(--bd-band)]',
  black: 'bg-[color:var(--bd-band-black)]',
};

/** Vertical padding per rhythm step, for the two padding models. */
const SPACE_FLAT: Record<BandSpace, string> = {
  none: '',
  sm: 'py-14 sm:py-16',
  md: 'py-20 sm:py-24',
  lg: 'py-24 sm:py-[104px]',
};

const SPACE_INSET: Record<BandSpace, string> = {
  none: '',
  sm: 'p-7 sm:p-9',
  md: 'p-8 sm:p-12',
  lg: 'p-9 sm:p-14',
};

/**
 * The horizontal half of {@link SPACE_INSET} — what a card-aligned stripe pads
 * its column by, so its content lands where a card of the same `space` puts it.
 *
 * A separate table rather than a derivation because Tailwind needs whole class
 * names to statically extract; `p-9`.replace('p-','px-') at runtime produces a
 * class the stylesheet never generated. `Band.test.tsx` does that derivation on
 * the RENDERED output instead, so the two cannot drift even though only one of
 * them can be written by hand.
 */
const INSET_X: Record<BandSpace, string> = {
  none: '',
  sm: 'px-7 sm:px-9',
  md: 'px-8 sm:px-12',
  lg: 'px-9 sm:px-14',
};

/** The page gutter — the outer step both a card and a card-aligned stripe take. */
const GUTTER = 'px-5 sm:px-8';

/** `band` is a rounded inset card; every other tone is a flat stripe. */
function isInset(tone: BandTone): boolean {
  return tone === 'band';
}

export function Band({
  tone = 'page',
  space = 'lg',
  align = 'stripe',
  as: Tag = 'section',
  id,
  ambient = false,
  className,
  outerClassName,
  style,
  children,
  ...aria
}: BandProps): ReactElement {
  const inset = isInset(tone);
  const dark = tone === 'band' || tone === 'black';
  const showAmbient = ambient && dark;
  // An inset tone is already on the card rule, so asking for it again is a
  // no-op rather than a second helping of padding.
  const cardAligned = !inset && align === 'card';

  // An inset band paints its ground on the INNER card so the page shows
  // through around it; a flat band paints the full-bleed stripe.
  // The ground stays on the OUTER element for a stripe even when it is card
  // aligned: padding does not clip a background, so the band still bleeds
  // edge to edge while its content steps inward.
  const outer = [
    'relative',
    inset ? 'bg-[color:var(--bd-page)]' : GROUND[tone],
    inset || cardAligned ? GUTTER : '',
    outerClassName,
  ]
    .filter(Boolean)
    .join(' ');

  const inner = [
    'relative mx-auto w-full max-w-[var(--bd-maxw)]',
    inset
      ? `overflow-hidden rounded-[var(--bd-radius-lg)] ${GROUND[tone]} ${SPACE_INSET[space]}`
      : `${cardAligned ? INSET_X[space] : GUTTER} ${SPACE_FLAT[space]}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Tag className={outer} id={id} style={style} {...aria}>
      <div className={inner}>
        {showAmbient ? <BandAmbient /> : null}
        {/* `relative` lifts content above the ambient layer without a z-index
            arms race — the ambient is the only absolutely-positioned sibling. */}
        <div className="relative">{children}</div>
      </div>
    </Tag>
  );
}

/**
 * The ambient decoration behind a dark band: a slow brand wash plus a masked
 * dot field. Split out so `Band` reads as layout and this reads as paint.
 */
function BandAmbient(): ReactElement {
  return (
    <>
      <div
        aria-hidden="true"
        data-bd-anim
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(62% 46% at 16% 0%, rgba(43,98,255,.24), transparent 70%),' +
            'radial-gradient(50% 42% at 94% 26%, rgba(181,17,224,.15), transparent 72%),' +
            'radial-gradient(40% 34% at 60% 100%, rgba(22,210,255,.09), transparent 74%)',
          animation: 'bdGlow 13s ease-in-out infinite',
        }}
      />
      <div
        aria-hidden="true"
        className="bd-band-dots pointer-events-none absolute inset-0 opacity-40"
      />
    </>
  );
}
